import {useState, lazy, Suspense, useRef, useCallback} from 'react'
import {AgentIcon, type Icon} from '@primer/octicons-react'
import {Button, IconButton, Spinner} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import type {Repository} from '@github-ui/current-repository'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import type {CreateTaskReference} from '@github-ui/agent-sessions/components/new-task-chat-input/NewTaskChatInput'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'

export type NewAgentTaskButtonProps = {
  isIconButton?: boolean
  icon?: Icon
  open?: boolean
  label?: string
  onOpen?: () => void
  onClose?: () => void
  setContentOpen?: (newOpen: boolean) => void
  currentRepo?: Repository
  currentReference?: CreateTaskReference
  hideRepoSelector?: boolean
  hideIcon?: boolean
  eventType: string
  size?: 'small' | 'medium' | 'large'
  tooltipDirection?: 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw'
  variant?: 'default' | 'primary' | 'danger' | 'invisible'
}

export {NewAgentTaskButton}
const LazyNewAgentTaskButtonContent = lazy(() =>
  // eslint-disable-next-line github/no-then
  import('./NewAgentTaskButtonContent').then(m => ({default: m.NewAgentTaskButtonContent})),
)

function NewAgentTaskButton({
  isIconButton,
  icon = AgentIcon,
  label = 'New agent session',
  onOpen,
  onClose,
  setContentOpen,
  open,
  currentRepo,
  currentReference,
  hideRepoSelector,
  eventType,
  size,
  tooltipDirection,
  variant,
}: NewAgentTaskButtonProps) {
  const [isOpen, setOpen] = useState(!!open)
  const {isServer: isSSR} = useRuntimeEnvironment()
  const buttonRef = useRef<HTMLButtonElement>(null)

  const handleOpen = useCallback(() => {
    setOpen(true)
    onOpen?.()
  }, [onOpen])

  const handleClose = useCallback(() => {
    setOpen(false)
    onClose?.()
    setContentOpen?.(false)
  }, [onClose, setContentOpen])

  const dialogTitle = currentRepo ? `${label} in ${currentRepo.ownerLogin}/${currentRepo.name}` : label

  return (
    <>
      {isIconButton ? (
        <IconButton
          ref={buttonRef}
          icon={icon}
          aria-label={label}
          size={size}
          tooltipDirection={tooltipDirection}
          variant={variant}
          onClick={handleOpen}
        />
      ) : (
        <Button ref={buttonRef} onClick={handleOpen} leadingVisual={icon} size={size}>
          {label}
        </Button>
      )}
      {isOpen && !isSSR && (
        <Dialog title={dialogTitle} onClose={handleClose} returnFocusRef={buttonRef} position={{narrow: 'fullscreen'}}>
          <Dialog.Body className="p-0">
            <Suspense fallback={<FallbackContent />}>
              <LazyNewAgentTaskButtonContent
                currentRepo={currentRepo}
                currentReference={currentReference}
                hideRepoSelector={hideRepoSelector}
                setOpen={handleClose}
                eventType={eventType}
              />
            </Suspense>
          </Dialog.Body>
        </Dialog>
      )}
    </>
  )
}

function FallbackContent() {
  return (
    <div className="d-flex flex-column flex-items-center p-6">
      <Spinner srText="Loading…" />
    </div>
  )
}
